import { Position, SpsActionDescriptor, SpsOptionListOption } from "@spscommerce/ds-shared";
import clsx from "clsx";
import * as React from "react";
import * as scrollIntoViewImport from "scroll-into-view-if-needed";

import { getPosition } from "../utils/getPosition";
import { PortalContext, usePortal } from "../utils/usePortal";
import { propTypes, SpsOptionListProps } from "./SpsOptionListProps";
import { useOptionListOptions } from "./useOptionListOptions";
import { useOptionListSearch } from "./useOptionListSearch";

const scrollIntoView = (scrollIntoViewImport as any).default || scrollIntoViewImport;

function compareOptions(optionA, optionB, comparisonKey) {
    return comparisonKey
        ? (optionA && optionA[comparisonKey]) === (optionB && optionB[comparisonKey])
        : optionA === optionB;
}

export function SpsOptionList(props: SpsOptionListProps) {
    const {
        captionKey,
        comparisonKey,
        options,
        textKey,
        valueKey,
        zeroState,

        hideInlineSearch,
        onSearchChange,
        search,
        searchDebounce,
        searchPlaceholder,

        attachTo,
        className,
        conformWidth,
        id,
        isOpen: isOpenProp,
        keyDown,
        nullOption,
        onOptionListChanged,
        onOptionSelected,
        onPositionFlip,
        onSelfToggle,
        optionRole,
        positionOverride,
        selectedOption,
        specialAction,
        unsafelyReplaceClassName,
        ...rest
    } = props;

    const specialActionOption = React.useMemo(() =>
        specialAction
            ? new SpsOptionListOption<SpsActionDescriptor>(specialAction, {
                textKey: "label",
                captionKey: "caption"
            })
            : null
    , [specialAction]);

    const [highlightedOptionIndex, setHighlightedOptionIndex] = React.useState(-1);
    const [isOpen, setIsOpen] = React.useState(isOpenProp);

    const rootRef = React.useRef<HTMLDivElement>(null);
    const optionsRef = React.useRef<HTMLDivElement>(null);
    const highlightedOptionRef = React.useRef<HTMLAnchorElement>(null);

    const portal = usePortal("sps-option-list-portal");
    const portalContext = React.useContext(PortalContext);

    const [primePosition, altPosition] = positionOverride || [Position.BOTTOM_LEFT, Position.TOP_LEFT];
    const [posStyle, openingUpward] = isOpen
        ? getPosition(
            rootRef,
            attachTo,
            primePosition,
            {
                altPosition,
                setMinWidth: true,
                setWidth: conformWidth,
                ...portalContext,
            }
        )
        : [{}, false];

    const [searchElement, searchState, searchStatePatch] = useOptionListSearch(props);
    const [optionList, anyOptionHasIcon] = useOptionListOptions(props, searchState, searchStatePatch);

    const classes = clsx(
        unsafelyReplaceClassName || "sps-option-list",
        "z-stratum-dropdown",
        isOpen && "sps-option-list--open",
        searchState.isAsync && "sps-option-list--searchable",
        openingUpward && "sps-option-list--opens-upward",
        specialAction && specialAction.label && "sps-option-list--has-special-action",
        className,
    );

    const openSelf = React.useCallback(() => {
        if (!isOpen) {
            setIsOpen(true);
            setHighlightedOptionIndex(-1);

            if (onSelfToggle) {
                onSelfToggle(true);
            }
        }
    }, [isOpen, onSelfToggle]);

    const closeSelf = React.useCallback(() => {
        if (isOpen) {
            setIsOpen(false);
            setHighlightedOptionIndex(-1);

            if (onSelfToggle) {
                onSelfToggle(false);
            }
        }
    }, [isOpen, onSelfToggle]);

    const selectOption = React.useCallback(option => {
        if (option && !option.disabled) {
            if (typeof option.value === "function") {
                (option.value as any)();
            } else if (typeof onOptionSelected === "function") {
                onOptionSelected(option.value);
            }
            closeSelf();
        }
    }, [onOptionSelected, closeSelf]);

    const handleOptionClick = React.useCallback((event: React.MouseEvent, option) => {
        event.stopPropagation();
        selectOption(option);
    }, [selectOption]);

    const handleKeyDown = React.useCallback((event: React.KeyboardEvent) => {
        if (!event) {
            return;
        }

        switch (event.key) {
            case "Tab":
            case "Escape":
                closeSelf();
                break;

                case "Enter":
                    if (highlightedOptionIndex > -1) {
                        const highlightedOption = optionList[highlightedOptionIndex] || specialActionOption;
                        event.preventDefault();
                        if (
                            typeof onOptionSelected === "function" &&
                            typeof highlightedOption.value === "function"
                        ) {
                            (highlightedOption.value as any)();
                        } else {
                            selectOption(highlightedOption);
                        }
                    }
                    break;
            case "Up":
            case "ArrowUp": {
                let newHighlightIndex = highlightedOptionIndex;
                if (openingUpward) {
                    if (highlightedOptionIndex <= -1) {
                        // From no highlight, go up to the last option
                        newHighlightIndex = optionList.length - 1;
                    } else if (highlightedOptionIndex === 0) {
                        // From the first option, if there's a special action to go up to, go up to it
                        // Otherwise do not go up, we're at the top
                        if (specialAction) {
                            newHighlightIndex = optionList.length;
                        }
                    } else if (highlightedOptionIndex < optionList.length) {
                        // From an option lower than the 0th (index > 0) and not at the special action
                        // (index === length of option list), go up one
                        newHighlightIndex = highlightedOptionIndex - 1;
                    }
                } else if (highlightedOptionIndex > -1) {
                    // When opening downward (default) the very top is "no highlight", when index === -1,
                    // and indices get larger as you go downward. So just go up one
                    newHighlightIndex = highlightedOptionIndex - 1;
                }

                if (newHighlightIndex !== highlightedOptionIndex) {
                    event.preventDefault();
                    setHighlightedOptionIndex(newHighlightIndex);
                }
                break;
            }

            case "Down":
            case "ArrowDown": {
                openSelf();

                let newHighlightIndex = highlightedOptionIndex;
                if (openingUpward) {
                    if (highlightedOptionIndex !== -1) {
                        if (highlightedOptionIndex >= optionList.length) {
                            // From the special action, go down to the first option
                            newHighlightIndex = 0;
                        } else if (highlightedOptionIndex === optionList.length - 1) {
                            // From the last option, go to "no highlight" (index === -1)
                            newHighlightIndex = -1;
                        } else {
                            // Otherwise, go down one
                            newHighlightIndex = highlightedOptionIndex + 1;
                        }
                    }
                } else if (highlightedOptionIndex < optionList.length - 1 + +!!specialAction) {
                    newHighlightIndex = highlightedOptionIndex + 1;
                }

                if (newHighlightIndex !== highlightedOptionIndex) {
                    event.preventDefault();
                    setHighlightedOptionIndex(newHighlightIndex);
                }
                break;
            }

            default:
                openSelf();
        }
    }, [closeSelf, openSelf, highlightedOptionIndex, selectOption, onOptionSelected, openingUpward, optionList]);

    React.useEffect(() => {
        if (highlightedOptionRef.current) {
            scrollIntoView(highlightedOptionRef.current, {
                scrollMode: "if-needed",
                block: "nearest",
                inline: "nearest"
            });
        }
    }, [highlightedOptionIndex]);

    React.useEffect(() => {
        setIsOpen(isOpenProp);
        if (!isOpenProp) {
            setHighlightedOptionIndex(-1);
        }
    }, [isOpenProp]);

    React.useEffect(() => {
        handleKeyDown(keyDown);
    }, [keyDown]);

    React.useEffect(() => {
        if (isOpen && onPositionFlip) {
            onPositionFlip(openingUpward);
        }
    }, [isOpen]);

    return portal(
        <div className={classes}
            id={id}
          aria-activedescendant={highlightedOptionIndex > -1
            ? `${id}-option-${highlightedOptionIndex}`
            : null
          }
          ref={rootRef}
          style={posStyle}
          onMouseLeave={() => setHighlightedOptionIndex(-1)}
          {...rest}
        >
          {searchElement}
          <div className="sps-option-list__options" ref={optionsRef}>
            {zeroState && (searchState.value || !searchState.isAsync) && optionList.length === 0 &&
              <div className="sps-option-list__zero-state">{zeroState}</div>
            }
            {optionList.map((option, i) =>
              <a key={i}
                id={`${id}-option-${i}`}
                role={optionRole}
                aria-selected={compareOptions(option.value, selectedOption, comparisonKey)}
                href={option.href}
                className={clsx(
                  "sps-option-list__option",
                  option.caption && "sps-option-list__option--has-caption",
                  option.disabled && "sps-option-list__option--disabled",
                  compareOptions(option.value, selectedOption, comparisonKey) && "sps-option-list__option--selected",
                  highlightedOptionIndex === i && "sps-option-list__option--highlighted",
                )}
                onClick={event => handleOptionClick(event, option)}
                onMouseOver={() => setHighlightedOptionIndex(i)}
                tabIndex={-1}
                ref={highlightedOptionIndex === i ? highlightedOptionRef : null}
              >
                {option.value && option.value.icon &&
                  <i className={clsx(
                    "sps-icon",
                    "sps-option-list__option-icon",
                    `sps-icon-${option.value.icon}`,
                  )}></i>
                }
                {!option.value || !option.value.icon && anyOptionHasIcon &&
                  <span className="sps-option-list__option-icon-spacer"></span>
                }
                <span dangerouslySetInnerHTML={{ __html: option.getHtml(searchState.replacementPattern) }}></span>
                {option.caption && <div className="sps-option-list__option-caption">{option.caption}</div>}
              </a>
            )}
          </div>
          {specialActionOption && specialAction.label &&
            <a className={clsx(
                "sps-option-list__option",
                "sps-option-list__special-action",
                highlightedOptionIndex === optionList.length && "sps-option-list__option--highlighted"
              )}
              href={specialActionOption.href}
              onClick={event => handleOptionClick(event, specialActionOption)}
              onMouseOver={() => setHighlightedOptionIndex(optionList.length)}>
              {specialAction.icon &&
                <i className={clsx(
                    "sps-icon",
                    "sps-option-list__option-icon",
                    `sps-icon-${specialAction.icon}`
                )}></i>
              }
              {!specialAction.icon && anyOptionHasIcon &&
                <span className="sps-option-list__option-icon-spacer"></span>
              }
              <span>{specialAction.label}</span>
            </a>
          }
        </div>
    );
}

Object.assign(SpsOptionList, {
    propTypes,
    displayName: "SpsOptionList"
});
